/** Facilities / Structure - the organisation as a tree, with a total on every node (ADR-0212).
 *
 * Read-only: companies under their parents (from the effective-dated ownership edges), each company's
 * sites under it, and what each node adds up to by scope in the period. Every number is the server's
 * (`GET /org/tree`), which reconciles to `GET /inventory` by construction; a node with no figure in a
 * scope shows a dash, **never a zero** (the tree's own rule: zero figures means "not assessed", not 0).
 * A company held by two parents appears under both, so rows are not additive and the totals row is
 * the workspace's, not a sum of the rows.
 */

import { TreeStructure } from "@phosphor-icons/react";
import { useState } from "react";

import { useFacilities } from "../../api/evidence";
import { useOrgTree } from "../../api/vendorSummary";
import type { OrgTree } from "../../api/vendorSummary";
import { ReadFailed } from "../../components/Loaded";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { tonnes } from "../../lib/format";
import { NoPeriod } from "../NoPeriod";
import { OpenFigures, count, matches, usePlaceLabel } from "../tableKit";
import type { Screen } from "../useScreen";
import styles from "./Facilities.module.css";

interface Node {
  key: string;
  /** The node as the breakdown names it: `site-<id>` or `company-<id>`. */
  ref: string;
  depth: number;
  kind: "Company" | "Site";
  name: string;
  where: string;
  scopes: readonly (string | null)[];
  total: string;
}

function cells(totals: { by_scope: ReadonlyArray<{ kgco2e: string; figures: number }> }): (string | null)[] {
  return totals.by_scope.map((cell) => (cell.figures > 0 ? cell.kgco2e : null));
}

/** The tree flattened to rows, depth first from the roots, a company's sites under it. A company the
 * walk did not reach (held by no edge from a root) is listed after, so none is dropped. */
export function flatten(tree: OrgTree, siteNames: ReadonlyMap<string, string>): Node[] {
  const entityById = new Map(tree.entities.map((e) => [e.entity_id, e]));
  const siteById = new Map(tree.facilities.map((f) => [f.facility_id, f]));
  const children = new Map<string, string[]>();
  for (const edge of tree.edges) {
    children.set(edge.parent_entity_id, [...(children.get(edge.parent_entity_id) ?? []), edge.child_entity_id]);
  }
  const out: Node[] = [];
  const seen = new Set<string>();
  const walk = (id: string, depth: number, path: readonly string[]) => {
    const entity = entityById.get(id);
    if (!entity || path.includes(id)) return;
    seen.add(id);
    out.push({
      key: `${path.join("/")}/${id}`,
      ref: `company-${id}`,
      depth,
      kind: "Company",
      name: entity.legal_name,
      where: entity.jurisdiction,
      scopes: cells(entity.totals),
      total: entity.totals.figures > 0 ? entity.totals.kgco2e : "",
    });
    for (const siteId of entity.facility_ids) {
      const site = siteById.get(siteId);
      if (!site) continue;
      out.push({
        key: `${path.join("/")}/${id}/${siteId}`,
        ref: `site-${siteId}`,
        depth: depth + 1,
        kind: "Site",
        name: siteNames.get(siteId) ?? site.name,
        where: site.retired ? `${site.jurisdiction} · retired` : site.jurisdiction,
        scopes: cells(site.totals),
        total: site.totals.figures > 0 ? site.totals.kgco2e : "",
      });
    }
    for (const child of children.get(id) ?? []) walk(child, depth + 1, [...path, id]);
  };
  for (const root of tree.roots) walk(root, 0, []);
  for (const entity of tree.entities) if (!seen.has(entity.entity_id)) walk(entity.entity_id, 0, []);
  return out;
}

export function StructureTab({ screen }: { screen: Screen }) {
  const label = usePlaceLabel();
  const window = screen.window;
  const tree = useOrgTree(window?.period_start, window?.period_end, screen.method);
  const facilities = useFacilities();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const nodes = tree.data ? flatten(tree.data, new Map((facilities.data ?? []).map((f) => [f.id, f.name]))) : [];
  const shown = nodes.filter((n) => matches(filters.search, n.name, n.where, n.kind));
  const dash = (value: string | null | undefined) => (value ? tonnes(value, 1) : "—");
  /** A node's tonnage, opening the figures behind it; a scope with no figure is a dash and opens nothing. */
  const figure = (ref: string, scope: string, value: string | null | undefined, name: string) => (
    <OpenFigures screen={screen} nodeKey={`${ref}${scope}`} value={dash(value)} name={name} />
  );

  const columns: GridColumn<Node>[] = [
    {
      id: "name",
      header: "Name",
      cell: (n) => <span className={styles[`indent${Math.min(n.depth, 3)}` as "indent0"]}>{n.name}</span>,
    },
    { id: "kind", header: "Kind", cell: (n) => <span className={styles.kind}>{n.kind}</span> },
    { id: "where", header: "Country", cell: (n) => n.where },
    { id: "s1", header: "Scope 1 tCO2e", numeric: true, cell: (n) => figure(n.ref, "-s1", n.scopes[0], n.name) },
    { id: "s2", header: "Scope 2 tCO2e", numeric: true, cell: (n) => figure(n.ref, "-s2", n.scopes[1], n.name) },
    { id: "s3", header: "Scope 3 tCO2e", numeric: true, cell: (n) => figure(n.ref, "-s3", n.scopes[2], n.name) },
    { id: "tco2e", header: "Total tCO2e", numeric: true, cell: (n) => figure(n.ref, "", n.total, n.name) },
  ];

  const workspace = tree.data?.workspace;
  const failed = window ? (tree.error ?? facilities.error) : facilities.error;

  return (
    <TablePage
      icon={TreeStructure}
      title="Facilities"
      label={label}
      actions={<InfoPopover infoKey="facilities.structure" />}
      tiles={
        tree.data ? (
          <TileStrip label="Structure summary">
            <Tile value={count(tree.data.entities.length)} label="Companies" />
            <Tile value={count(tree.data.facilities.length)} label="Sites" />
            <Tile
              value={workspace && workspace.figures > 0 ? tonnes(workspace.kgco2e, 1) : "—"}
              unit="tCO2e"
              label="Whole organisation"
              onOpen={workspace && workspace.figures > 0 ? () => screen.openBreakdown("all") : undefined}
            />
          </TileStrip>
        ) : undefined
      }
      filters={
        window ? (
          <FilterLine
            filters={[]}
            state={filters}
            onChange={setFilters}
            searchLabel="Search the structure"
            resultCount={tree.data ? { shown: shown.length, total: nodes.length, noun: "nodes" } : undefined}
          />
        ) : undefined
      }
      table={
        tree.data ? (
          <GridTable
            caption="Structure"
            columns={columns}
            rows={shown}
            rowKey={(n) => n.key}
            rowLabel={(n) => n.name}
            rowNumbers={false}
            totals={
              workspace
                ? {
                    label: "Whole organisation",
                    values: {
                      s1: figure("all", "-s1", workspace.by_scope[0]?.figures ? workspace.by_scope[0].kgco2e : null, "Whole organisation"),
                      s2: figure("all", "-s2", workspace.by_scope[1]?.figures ? workspace.by_scope[1].kgco2e : null, "Whole organisation"),
                      s3: figure("all", "-s3", workspace.by_scope[2]?.figures ? workspace.by_scope[2].kgco2e : null, "Whole organisation"),
                      tco2e: figure("all", "", workspace.figures > 0 ? workspace.kgco2e : null, "Whole organisation"),
                    },
                  }
                : undefined
            }
            empty="Nothing in the structure yet."
            testId="structure-table"
          />
        ) : undefined
      }
      state={
        failed ? (
          <ReadFailed
            of="The structure"
            error={failed}
            onRetry={() => {
              void tree.refetch();
              void facilities.refetch();
            }}
          />
        ) : !window ? (
          <NoPeriod />
        ) : !tree.data ? (
          <GridTable
            caption="Structure"
            columns={[]}
            rows={[]}
            rowKey={(n: Node) => n.key}
            loading
            testId="structure-loading"
          />
        ) : undefined
      }
    />
  );
}
